import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { FileText, Plus, Loader2, Paperclip, Trash2, ExternalLink } from 'lucide-react';

type MerchantDocument = {
  id: number;
  doc_number: string;
  doc_date: string;
  file_name: string | null;
  has_file: boolean;
  is_active: boolean;
};

/** Do'kon ↔ Hisob MMT bosh shartnomasi — hisob-fakturalarda contractNum sifatida ishlatiladi (FR-83). */
export default function DocumentsTab() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const qc = useQueryClient();
  const [num, setNum] = useState('');
  const [date, setDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [toDelete, setToDelete] = useState<MerchantDocument | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['/merchant/documents'] });
  const docs = ((data as { data?: MerchantDocument[] } | undefined)?.data ?? []);

  const add = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append('doc_number', num.trim());
      body.append('doc_date', date);
      if (file) body.append('file', file);
      const { error } = await apiClient.POST('/merchant/documents', { body: body as unknown as never });
      if (error) throw new Error('message' in error ? String(error.message) : t('merchant_documents.save_error'));
    },
    onSuccess: () => {
      toast.success(t('merchant_documents.saved'));
      setNum(''); setDate(''); setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      qc.invalidateQueries({ queryKey: ['/merchant/documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/merchant/documents/{document}', { params: { path: { document: id } } });
      if (error) throw new Error(t('merchant_documents.save_error'));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/merchant/documents'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const openFile = async (id: number) => {
    const { data: res, error } = await apiClient.GET('/merchant/documents/{document}/file-url', { params: { path: { document: id } } });
    const url = (res as { data?: { url?: string } } | undefined)?.data?.url;
    if (error || !url) { toast.error(t('merchant_documents.file_error')); return; }
    window.open(url, '_blank', 'noopener');
  };

  const submit = () => {
    if (!num.trim() || !date) { toast.error(t('merchant_documents.required')); return; }
    add.mutate();
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={16} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t('merchant_documents.title')}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('merchant_documents.hint')}</p>
        {isOwner && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr_auto] gap-3 mb-4">
            <Input value={num} onChange={e => setNum(e.target.value)} placeholder={t('merchant_documents.number')} />
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
            <Input ref={fileInput} type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} />
            <Button onClick={submit} disabled={add.isPending}>
              {add.isPending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
              {t('common.save')}
            </Button>
          </div>
        )}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : docs.length === 0 ? (
          <p className="text-sm text-warning">{t('merchant_documents.empty')}</p>
        ) : (
          <ul className="divide-y divide-border">
            {docs.map(d => (
              <li key={d.id} className="py-2 flex flex-wrap items-center gap-3 text-sm">
                <span className="font-mono text-foreground">№ {d.doc_number}</span>
                <span className="text-muted-foreground">{new Date(d.doc_date).toLocaleDateString(i18n.language)}</span>
                {d.is_active
                  ? <Badge className="bg-success/15 text-success border-0 text-xs">{t('merchant_documents.active')}</Badge>
                  : <Badge variant="outline" className="text-xs">{t('merchant_documents.inactive')}</Badge>}
                <div className="ml-auto flex items-center gap-1">
                  {d.has_file && (
                    <Button variant="ghost" size="sm" onClick={() => openFile(d.id)}>
                      <Paperclip size={14} className="mr-1" />{d.file_name}<ExternalLink size={12} className="ml-1" />
                    </Button>
                  )}
                  {isOwner && (
                    <Button variant="ghost" size="icon" aria-label={t('merchant_documents.delete')} onClick={() => setToDelete(d)} disabled={remove.isPending}>
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={toDelete !== null} onOpenChange={open => { if (!open) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('merchant_documents.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('merchant_documents.delete_confirm', { number: toDelete?.doc_number ?? '' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (toDelete) remove.mutate(toDelete.id); setToDelete(null); }}
            >
              {t('merchant_documents.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
